// src/admin/CatechistCurriculumVitaeManager.js - Phân quyền: Admin (Toàn trường) & Trưởng Khối (Xem và Sửa trong Khối của mình)
import React, { useState, useContext, useMemo, useCallback } from 'react';
import { StudentContext } from '../StudentContext';
import { updateCatechist } from '../FirebaseService';
import * as XLSX from 'xlsx';
import './CatechistCurriculumVitaeManager.css';

// Cấu hình các trường thông tin có thể chỉnh sửa trực tiếp
const FIELD_CONFIGS = {
  holyName: { label: 'Tên Thánh', type: 'text' },
  fullName: { label: 'Họ và Tên', type: 'text', required: true },
  gender: { label: 'Giới tính', type: 'select', options: ['Nam', 'Nữ'] },
  dateOfBirth: { label: 'Ngày sinh', type: 'date' },
  phone: { label: 'Số điện thoại', type: 'tel' },
  email: { label: 'Email liên hệ', type: 'email' },
  address: { label: 'Địa chỉ cư ngụ', type: 'text' },
  profession: { label: 'Nghề nghiệp', type: 'text' },
  educationLevel: { label: 'Trình độ học vấn', type: 'text' },
  catechistLevel: { label: 'Cấp bậc GLV', type: 'select', options: ['Dự bị', 'Cấp 1', 'Cấp 2', 'Cấp 3'] },
  religious: { label: 'Thành phần', type: 'select', options: ['Giáo lý viên', 'Tu Sĩ'] },
  baptismDate: { label: 'Ngày Rửa Tội', type: 'date' },
  baptismPlace: { label: 'Nơi Rửa Tội', type: 'text' },
  confirmationDate: { label: 'Ngày Thêm Sức', type: 'date' },
  confirmationPlace: { label: 'Nơi Thêm Sức', type: 'text' },
  notes: { label: 'Ghi chú thêm', type: 'textarea' }
};

// Helper chuẩn hóa chuỗi Tiếng Việt để đối chiếu tên Khối & Lớp chính xác
const cleanVietnameseText = (str) => {
  if (!str) return '';
  let slug = str.toString().toLowerCase();
  slug = slug.replace(/đ/g, 'd');
  slug = slug.replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a');
  slug = slug.replace(/[éèẻẽẹêếềểễệ]/g, 'e');
  slug = slug.replace(/[íìỉĩị]/g, 'i');
  slug = slug.replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o');
  slug = slug.replace(/[úùủũụưứừửữự]/g, 'u');
  slug = slug.replace(/[ýỳỷỹỵ]/g, 'y');
  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  slug = slug.replace(/\b(khoi|nganh)\b/g, '');
  return slug.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
};

// Biểu tượng sinh động cho từng giá trị lựa chọn
const getOptionIcon = (fieldKey, opt) => {
  if (fieldKey === 'gender') {
    return opt === 'Nam' ? '👦' : '👧';
  }
  if (fieldKey === 'religious') {
    return opt === 'Tu Sĩ' ? '✝️' : '👔';
  }
  if (fieldKey === 'catechistLevel') {
    if (opt === 'Ban điều hành') return '🛡️';
    if (opt === 'Dự bị') return '🌱';
    return '⭐';
  }
  return '🔹';
};

const CatechistCurriculumVitaeManager = () => {
  const { catechistsData, updateLocalCatechist, allBlocks } = useContext(StudentContext);

  // Phân quyền người dùng: Admin hoặc Trưởng Khối đều có quyền chỉnh sửa trong phạm vi của mình
  const userRole = localStorage.getItem('userRole') || 'catechist';
  const blockLeader = localStorage.getItem('blockLeader') || '';
  const isAdmin = userRole === 'admin';
  const isBlockLeader = Boolean(blockLeader && blockLeader.trim());
  const canEdit = isAdmin || isBlockLeader;

  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState('ALL');
  const [filterLock, setFilterLock] = useState('ALL');
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');

  // Modal Sửa Nhanh từng ô khi bấm vào
  const [cellEditModal, setCellEditModal] = useState({
    open: false,
    catechist: null,
    fieldKey: '',
    value: ''
  });
  const [isSavingCell, setIsSavingCell] = useState(false);
  const [updatingLockUid, setUpdatingLockUid] = useState(null);

  // Kiểm tra Giáo lý viên có thuộc Khối của Trưởng Khối đang đăng nhập hay không
  const isCatechistInMyBlock = useCallback((c) => {
    if (isAdmin) return true;
    if (!isBlockLeader) return false;
    if (!c.className && !c.blockLeader) return false;

    const userBlockClean = cleanVietnameseText(blockLeader);
    if (!userBlockClean) return false;

    const catechistBlockClean = cleanVietnameseText(c.blockLeader);
    const catechistClassClean = cleanVietnameseText(c.className);
    const rawClassLower = (c.className || '').toLowerCase().trim();

    // 1. Khớp trực tiếp trường blockLeader của GLV
    if (
      catechistBlockClean &&
      (catechistBlockClean.includes(userBlockClean) || userBlockClean.includes(catechistBlockClean))
    ) {
      return true;
    }

    // 2. Khớp theo danh sách các lớp thuộc Khối trong Niên khóa hiện tại
    if (allBlocks && Object.keys(allBlocks).length > 0) {
      for (const groupCleanId of Object.keys(allBlocks)) {
        const classes = allBlocks[groupCleanId] || [];
        const groupNameClean = cleanVietnameseText(classes[0]?.group || groupCleanId);

        if (
          groupNameClean &&
          (groupNameClean.includes(userBlockClean) || userBlockClean.includes(groupNameClean))
        ) {
          const matchedClass = classes.some(
            (cls) =>
              (cls.name || '').toLowerCase().trim() === rawClassLower ||
              (cls.id || '').toLowerCase().trim() === rawClassLower ||
              cleanVietnameseText(cls.name) === catechistClassClean
          );
          if (matchedClass) return true;
        }
      }
    }

    // 3. Dự phòng: Tên lớp chứa tên khối (VD: "Khai Tâm 1A Nữ" chứa "Khai Tâm")
    if (catechistClassClean && catechistClassClean.includes(userBlockClean)) {
      return true;
    }

    return false;
  }, [isAdmin, isBlockLeader, blockLeader, allBlocks]);

  // Mở modal khi bấm trực tiếp vào ô thông tin (Cho phép cả Admin và Trưởng Khối)
  const handleCellClick = (catechist, fieldKey) => {
    if (!canEdit || !isCatechistInMyBlock(catechist)) return;

    const config = FIELD_CONFIGS[fieldKey];
    let initialValue = catechist[fieldKey];
    if (config?.type === 'select' && !initialValue && config.options?.length > 0) {
      initialValue = config.options[0];
    }

    setCellEditModal({
      open: true,
      catechist,
      fieldKey,
      value: initialValue || ''
    });
  };

  // Xác nhận lưu sửa ô (Cho phép cả Admin và Trưởng Khối)
  const handleSaveCell = async (e) => {
    e.preventDefault();
    if (!canEdit || !cellEditModal.catechist || !cellEditModal.fieldKey) return;
    if (!isCatechistInMyBlock(cellEditModal.catechist)) return;

    const { catechist, fieldKey, value } = cellEditModal;
    setIsSavingCell(true);
    try {
      const updates = { [fieldKey]: value };
      const res = await updateCatechist(catechist.uid, updates);
      if (res.success) {
        updateLocalCatechist(catechist.uid, updates);
        setCellEditModal({ open: false, catechist: null, fieldKey: '', value: '' });
      } else {
        alert('❌ ' + (res.message || 'Lỗi khi cập nhật.'));
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsSavingCell(false);
    }
  };

  // Bật/Tắt Khóa chỉnh sửa (Cho phép cả Admin và Trưởng Khối trong khối của mình)
  const handleToggleLock = async (catechist) => {
    if (!canEdit || !isCatechistInMyBlock(catechist)) return;

    const nextLock = !catechist.isProfileLocked;
    setUpdatingLockUid(catechist.uid);
    try {
      const res = await updateCatechist(catechist.uid, { isProfileLocked: nextLock });
      if (res.success) {
        updateLocalCatechist(catechist.uid, { isProfileLocked: nextLock });
      } else {
        alert('❌ Lỗi: ' + res.message);
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setUpdatingLockUid(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // Lọc và Sắp Xếp Danh Sách
  const displayList = useMemo(() => {
    if (!catechistsData) return [];
    let list = catechistsData.filter((c) => {
      if (c.status === 'inactive') return false;

      // 🌟 LỌC THEO KHỐI NẾU LÀ TRƯỞNG KHỐI 🌟
      if (!isCatechistInMyBlock(c)) return false;

      if (filterGender !== 'ALL' && (c.gender || 'Nam') !== filterGender) return false;
      if (filterLock === 'locked' && !c.isProfileLocked) return false;
      if (filterLock === 'unlocked' && c.isProfileLocked) return false;

      const term = searchTerm.toLowerCase().trim();
      return (
        (c.fullName && c.fullName.toLowerCase().includes(term)) ||
        (c.holyName && c.holyName.toLowerCase().includes(term)) ||
        (c.idCode && c.idCode.toLowerCase().includes(term)) ||
        (c.phone && c.phone.includes(term)) ||
        (c.address && c.address.toLowerCase().includes(term))
      );
    });

    list.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'fullName') {
        const getLastName = (name) => {
          if (!name) return '';
          const parts = name.trim().split(/\s+/);
          return parts[parts.length - 1].toLowerCase();
        };
        valA = getLastName(a.fullName);
        valB = getLastName(b.fullName);
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [catechistsData, filterGender, filterLock, searchTerm, sortField, sortDir, isCatechistInMyBlock]);

  const handleExportExcel = () => {
    const data = [
      [
        'STT', 'Mã ID', 'Tên Thánh', 'Họ và Tên', 'Giới Tính', 'Ngày Sinh',
        'Số Điện Thoại', 'Email', 'Địa Chỉ', 'Nghề Nghiệp', 'Trình Độ',
        'Cấp Bậc GLV', 'Thành Phần', 'Ngày Rửa Tội', 'Nơi Rửa Tội',
        'Ngày Thêm Sức', 'Nơi Thêm Sức', 'Ghi Chú', 'Trạng Thái Khóa'
      ]
    ];

    displayList.forEach((c, idx) => {
      data.push([
        idx + 1, c.idCode || '', c.holyName || '', c.fullName || '', c.gender || 'Nam', c.dateOfBirth || '',
        c.phone || '', c.email || '', c.address || '', c.profession || '', c.educationLevel || '',
        c.catechistLevel || '', c.religious || 'Giáo lý viên', c.baptismDate || '', c.baptismPlace || '',
        c.confirmationDate || '', c.confirmationPlace || '', c.notes || '',
        c.isProfileLocked ? 'Đã khóa' : 'Mở'
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'SoYeuLyLichGLV');
    XLSX.writeFile(wb, `SoYeuLyLich_GLV_${isAdmin ? 'ToanTruong' : (blockLeader || 'Khoi')}.xlsx`);
  };

  const currentFieldConfig = FIELD_CONFIGS[cellEditModal.fieldKey] || { label: cellEditModal.fieldKey, type: 'text' };

  return (
    <div className="ccvm-container">
      {/* HEADER */}
      <div className="ccvm-header">
        <div>
          <h2 className="ccvm-title">
            🗂️ Sơ Yếu Lý Lịch Giáo Lý Viên
            {!isAdmin && blockLeader && <span className="ccvm-block-tag">Khối: {blockLeader}</span>}
          </h2>
          <p className="ccvm-subtitle">
            {isAdmin
              ? '💡 Quản trị viên: Bấm trực tiếp vào bất kỳ ô thông tin nào để chỉnh sửa nhanh. Khóa chỉnh sửa sẽ ngăn GLV tự thay đổi hồ sơ.'
              : `💡 Trưởng khối ${blockLeader}: Bấm trực tiếp vào bất kỳ ô thông tin nào để cập nhật sơ yếu lý lịch cho Giáo lý viên thuộc khối của mình.`}
          </p>
        </div>
        <button onClick={handleExportExcel} className="ccvm-btn ccvm-btn-excel">
          📥 Xuất Excel ({displayList.length})
        </button>
      </div>

      {/* THANH CÔNG CỤ */}
      <div className="ccvm-toolbar-card">
        <div className="ccvm-filter-group">
          <label className="ccvm-label">Giới tính:</label>
          <select
            value={filterGender}
            onChange={(e) => setFilterGender(e.target.value)}
            className="ccvm-select"
          >
            <option value="ALL">-- Tất cả --</option>
            <option value="Nam">Nam</option>
            <option value="Nữ">Nữ</option>
          </select>

          <label className="ccvm-label">Trạng thái khóa:</label>
          <select
            value={filterLock}
            onChange={(e) => setFilterLock(e.target.value)}
            className="ccvm-select"
          >
            <option value="ALL">-- Tất cả --</option>
            <option value="locked">🔒 Đã khóa sửa</option>
            <option value="unlocked">🔓 Đang mở</option>
          </select>

          <input
            type="text"
            placeholder="Tìm tên, ID, SĐT, địa chỉ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ccvm-search-input"
          />
        </div>
      </div>

      {/* BẢNG ĐẦY ĐỦ CÁC CỘT */}
      <div className="ccvm-table-wrapper">
        <table className="ccvm-table">
          <thead>
            <tr>
              <th>STT</th>
              <th onClick={() => handleSort('idCode')} className="ccvm-sortable">
                Mã ID{getSortIcon('idCode')}
              </th>
              <th onClick={() => handleSort('holyName')} className="ccvm-sortable">
                Tên Thánh{getSortIcon('holyName')}
              </th>
              <th onClick={() => handleSort('fullName')} className="ccvm-sticky-head ccvm-sortable">
                Họ và Tên{getSortIcon('fullName')}
              </th>
              <th onClick={() => handleSort('gender')} className="ccvm-sortable ccvm-text-center">
                Giới Tính{getSortIcon('gender')}
              </th>
              <th>Ngày Sinh</th>
              <th>Số Điện Thoại</th>
              <th>Email</th>
              <th>Địa Chỉ Cư Ngụ</th>
              <th>Nghề Nghiệp</th>
              <th>Học Vấn</th>
              <th>Cấp Bậc GLV</th>
              <th>Thành Phần</th>
              <th>Ngày Rửa Tội</th>
              <th>Nơi Rửa Tội</th>
              <th>Ngày Thêm Sức</th>
              <th>Nơi Thêm Sức</th>
              <th>Ghi Chú</th>
              <th className="ccvm-text-center">Khóa Sửa</th>
            </tr>
          </thead>
          <tbody>
            {displayList.length === 0 ? (
              <tr>
                <td colSpan="19" className="ccvm-empty-td">
                  Không tìm thấy Giáo lý viên nào trong khối phù hợp với bộ lọc.
                </td>
              </tr>
            ) : (
              displayList.map((c, idx) => (
                <tr key={c.uid || idx}>
                  <td className="ccvm-text-center">{idx + 1}</td>
                  <td className="ccvm-id-code">{c.idCode || '-'}</td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'holyName') : undefined}
                    title={canEdit ? 'Bấm để sửa Tên Thánh' : ''}
                  >
                    {c.holyName || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={`ccvm-sticky-col ${canEdit ? 'ccvm-editable-cell' : ''}`}
                    onClick={canEdit ? () => handleCellClick(c, 'fullName') : undefined}
                    title={canEdit ? 'Bấm để sửa Họ và Tên' : ''}
                  >
                    {c.fullName}
                  </td>

                  <td
                    className={`ccvm-text-center ${canEdit ? 'ccvm-editable-cell' : ''}`}
                    onClick={canEdit ? () => handleCellClick(c, 'gender') : undefined}
                    title={canEdit ? 'Bấm để sửa Giới tính' : ''}
                  >
                    <span className={`ccvm-gender-badge ${c.gender === 'Nữ' ? 'nu' : 'nam'}`}>
                      {c.gender || 'Nam'}
                    </span>
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'dateOfBirth') : undefined}
                    title={canEdit ? 'Bấm để sửa Ngày sinh' : ''}
                  >
                    {c.dateOfBirth || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    style={{ fontFamily: 'monospace' }}
                    onClick={canEdit ? () => handleCellClick(c, 'phone') : undefined}
                    title={canEdit ? 'Bấm để sửa Số điện thoại' : ''}
                  >
                    {c.phone || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'email') : undefined}
                    title={canEdit ? 'Bấm để sửa Email' : ''}
                  >
                    {c.email || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={`ccvm-ellipsis ${canEdit ? 'ccvm-editable-cell' : ''}`}
                    onClick={canEdit ? () => handleCellClick(c, 'address') : undefined}
                    title={canEdit ? 'Bấm để sửa Địa chỉ' : (c.address || '')}
                  >
                    {c.address || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'profession') : undefined}
                    title={canEdit ? 'Bấm để sửa Nghề nghiệp' : ''}
                  >
                    {c.profession || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'educationLevel') : undefined}
                    title={canEdit ? 'Bấm để sửa Học vấn' : ''}
                  >
                    {c.educationLevel || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'catechistLevel') : undefined}
                    title={canEdit ? 'Bấm để sửa Cấp bậc GLV' : ''}
                  >
                    <span className="ccvm-level-badge">{c.catechistLevel || 'Dự bị'}</span>
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'religious') : undefined}
                    title={canEdit ? 'Bấm để sửa Thành phần' : ''}
                  >
                    {c.religious || 'Giáo lý viên'}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'baptismDate') : undefined}
                    title={canEdit ? 'Bấm để sửa Ngày Rửa Tội' : ''}
                  >
                    {c.baptismDate || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'baptismPlace') : undefined}
                    title={canEdit ? 'Bấm để sửa Nơi Rửa Tội' : ''}
                  >
                    {c.baptismPlace || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'confirmationDate') : undefined}
                    title={canEdit ? 'Bấm để sửa Ngày Thêm Sức' : ''}
                  >
                    {c.confirmationDate || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={canEdit ? 'ccvm-editable-cell' : ''}
                    onClick={canEdit ? () => handleCellClick(c, 'confirmationPlace') : undefined}
                    title={canEdit ? 'Bấm để sửa Nơi Thêm Sức' : ''}
                  >
                    {c.confirmationPlace || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  <td
                    className={`ccvm-ellipsis ${canEdit ? 'ccvm-editable-cell' : ''}`}
                    onClick={canEdit ? () => handleCellClick(c, 'notes') : undefined}
                    title={canEdit ? 'Bấm để sửa Ghi chú' : (c.notes || '')}
                  >
                    {c.notes || <span className="ccvm-placeholder-text">Chưa có</span>}
                  </td>

                  {/* NÚT KHÓA / MỞ KHÓA */}
                  <td className="ccvm-text-center">
                    <button
                      type="button"
                      onClick={() => handleToggleLock(c)}
                      disabled={!canEdit || updatingLockUid === c.uid}
                      className={`ccvm-lock-toggle-btn ${c.isProfileLocked ? 'locked' : 'unlocked'}`}
                      title={
                        c.isProfileLocked
                          ? 'Bấm để MỞ cho GLV sửa'
                          : 'Bấm để KHÓA không cho GLV sửa'
                      }
                    >
                      {updatingLockUid === c.uid ? '⏳' : c.isProfileLocked ? '🔒 Đã khóa' : '🔓 Mở'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* POPUP SỬA NHANH TRƯỜNG DỮ LIỆU ĐƯỢC CHỌN (DÀNH CHO ADMIN & TRƯỞNG KHỐI) */}
      {canEdit && cellEditModal.open && cellEditModal.catechist && (
        <div className="ccvm-modal-overlay">
          <div className="ccvm-modal-card-small">
            <h3 className="ccvm-modal-title">
              ✏️ Cập Nhật {currentFieldConfig.label}
            </h3>
            <p className="ccvm-modal-subtitle">
              Giáo lý viên: <strong>{cellEditModal.catechist.holyName} {cellEditModal.catechist.fullName}</strong> ({cellEditModal.catechist.idCode})
            </p>

            <form onSubmit={handleSaveCell} className="ccvm-modal-form">
              <div className="ccvm-form-group">
                <label>Chọn {currentFieldConfig.label}:</label>

                {currentFieldConfig.type === 'select' ? (
                  <div className={`ccvm-options-grid ${currentFieldConfig.options.length <= 2 ? 'grid-2-cols' : ''}`}>
                    {currentFieldConfig.options.map((opt) => {
                      const isSelected = cellEditModal.value === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          className={`ccvm-option-card ${isSelected ? 'selected' : ''} ${
                            cellEditModal.fieldKey === 'gender' ? (opt === 'Nam' ? 'opt-nam' : 'opt-nu') : ''
                          }`}
                          onClick={() => setCellEditModal({ ...cellEditModal, value: opt })}
                        >
                          <div className="ccvm-option-content">
                            <span className="ccvm-option-radio">{isSelected ? '●' : '○'}</span>
                            <span className="ccvm-option-label">
                              {getOptionIcon(cellEditModal.fieldKey, opt)} {opt}
                            </span>
                          </div>
                          {isSelected && <span className="ccvm-option-badge">✓ Đã chọn</span>}
                        </button>
                      );
                    })}
                  </div>
                ) : currentFieldConfig.type === 'textarea' ? (
                  <textarea
                    rows="3"
                    value={cellEditModal.value}
                    onChange={(e) => setCellEditModal({ ...cellEditModal, value: e.target.value })}
                    className="ccvm-textarea"
                    placeholder={`Nhập ${currentFieldConfig.label.toLowerCase()}...`}
                    autoFocus
                  />
                ) : (
                  <input
                    type={currentFieldConfig.type}
                    value={cellEditModal.value}
                    onChange={(e) => setCellEditModal({ ...cellEditModal, value: e.target.value })}
                    className="ccvm-input"
                    placeholder={`Nhập ${currentFieldConfig.label.toLowerCase()}...`}
                    required={Boolean(currentFieldConfig.required)}
                    autoFocus
                  />
                )}
              </div>

              <div className="ccvm-modal-actions">
                <button
                  type="button"
                  onClick={() => setCellEditModal({ open: false, catechist: null, fieldKey: '', value: '' })}
                  disabled={isSavingCell}
                  className="ccvm-btn-cancel"
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  disabled={isSavingCell}
                  className="ccvm-btn-save"
                >
                  {isSavingCell ? '⏳ Đang lưu...' : '💾 Cập Nhật Ngay'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatechistCurriculumVitaeManager;